"use client";

import { ChevronDown } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { cn } from "@/lib/utils";

interface CollapsibleSectionProps {
  id: string;
  title: string;
  description?: string;
  meta?: React.ReactNode;
  defaultOpen?: boolean;
  storageKey?: string;
  className?: string;
  /**
   * Legacy in-page anchor ids that external code (e.g. score radar) scrolls
   * to via `document.getElementById`. Rendered as zero-height anchors inside
   * the section so they exist in the DOM even when collapsed. `scrollToAnchor`
   * in score-radar also clicks the toggle button before scrolling to
   * auto-expand.
   */
  anchorIds?: string[];
  children: React.ReactNode;
}

function readStored(storageKey: string, id: string): boolean | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Record<string, boolean>;
    return typeof parsed?.[id] === "boolean" ? parsed[id] : null;
  } catch {
    return null;
  }
}

function writeStored(storageKey: string, id: string, open: boolean) {
  if (typeof window === "undefined") return;
  try {
    const raw = window.localStorage.getItem(storageKey);
    const parsed = (raw ? JSON.parse(raw) : {}) as Record<string, boolean>;
    parsed[id] = open;
    window.localStorage.setItem(storageKey, JSON.stringify(parsed));
  } catch {
    // ignore quota / serialization failures
  }
}

function CollapsibleSection({
  id,
  title,
  description,
  meta,
  defaultOpen = false,
  storageKey,
  className,
  anchorIds,
  children,
}: CollapsibleSectionProps) {
  const [open, setOpen] = useState(defaultOpen);

  useEffect(() => {
    if (!storageKey) return;
    const stored = readStored(storageKey, id);
    if (stored !== null) setOpen(stored);
  }, [storageKey, id]);

  const toggle = useCallback(() => {
    setOpen((prev) => {
      const next = !prev;
      if (storageKey) writeStored(storageKey, id, next);
      return next;
    });
  }, [id, storageKey]);

  const panelId = `${id}-panel`;
  const headerId = `${id}-header`;

  return (
    <section
      className={cn(
        "relative rounded-[var(--radius-xl)] border border-[var(--color-border-default)]/70 bg-[var(--color-bg-card)]/40",
        className,
      )}
    >
      {anchorIds?.map((aid) => (
        <span
          key={aid}
          id={aid}
          aria-hidden="true"
          className="block h-0 scroll-mt-24"
        />
      ))}
      <button
        type="button"
        id={headerId}
        onClick={toggle}
        aria-expanded={open}
        aria-controls={panelId}
        className="flex w-full items-start justify-between gap-[var(--space-4)] px-[var(--space-5)] py-[var(--space-4)] text-left transition-colors hover:bg-[var(--color-bg-muted)]/40 rounded-[var(--radius-xl)]"
      >
        <div className="min-w-0 flex-1">
          <h2 className="font-heading text-[length:var(--font-size-xl)] font-[number:var(--font-weight-bold)] tracking-[var(--letter-spacing-tight)] text-[var(--color-text-primary)]">
            {title}
          </h2>
          {description && (
            <p className="mt-[var(--space-1)] text-[length:var(--font-size-sm)] text-[var(--color-text-muted)]">
              {description}
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-[var(--space-3)] pt-[var(--space-1)]">
          {meta && (
            <span className="text-[length:var(--font-size-xs)] text-[var(--color-text-muted)]">
              {meta}
            </span>
          )}
          <ChevronDown
            className={cn(
              "size-5 text-[var(--color-text-muted)] transition-transform",
              open && "rotate-180",
            )}
            aria-hidden="true"
          />
        </div>
      </button>
      {/*
        AnimatePresence + height: 0 → "auto" gives the open/close a real
        sweep instead of the instant pop the unstyled `{open && ...}`
        produced. `overflow: hidden` on the outer wrapper clips children
        while the height tweens; we lift it back to `visible` after the
        open animation lands so absolutely-positioned descendants
        (tooltips, dropdowns) aren't trapped inside the panel once it's
        fully expanded.
      */}
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            id={panelId}
            role="region"
            aria-labelledby={headerId}
            key="panel"
            initial={{ height: 0, opacity: 0 }}
            animate={{
              height: "auto",
              opacity: 1,
              transitionEnd: { overflow: "visible" },
            }}
            exit={{ height: 0, opacity: 0, overflow: "hidden" }}
            transition={{
              height: { type: "spring", stiffness: 200, damping: 30 },
              opacity: { duration: 0.18 },
            }}
            style={{ overflow: "hidden" }}
          >
            <div className="space-y-[var(--space-6)] border-t border-[var(--color-border-default)]/60 px-[var(--space-5)] py-[var(--space-5)] sm:px-[var(--space-6)] sm:py-[var(--space-6)]">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

export { CollapsibleSection };
export type { CollapsibleSectionProps };
